import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Zap, Brain, Wrench, Sparkles, DollarSign, Star } from 'lucide-react';
import { cn } from '@/utils/cn';
import { modelsApi, type AiModelInfo } from '@/api/models';

interface ModelSelectorProps {
  value: string;
  onChange: (modelId: string) => void;
  className?: string;
}

// Fallback models in case API fails (DeepSeek is global default)
const FALLBACK_MODELS: AiModelInfo[] = [
  {
    id: 'deepseek-chat',
    name: 'DeepSeek V3.2',
    provider: 'Deepseek',
    contextWindow: 128000,
    maxOutputTokens: 8192,
    supportsTools: false,
    supportsThinking: false,
    speedTier: 'fast',
    costTier: 1,
    description: 'Latest flagship model with 128K context, fast and efficient',
    isDefault: true,
    isGlobalDefault: true,
  },
  {
    id: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    provider: 'Claude',
    contextWindow: 200000,
    maxOutputTokens: 16000,
    supportsTools: true,
    supportsThinking: true,
    speedTier: 'fast',
    costTier: 3,
    description: 'Balanced for general use, coding, and agents',
    isDefault: true,
    isGlobalDefault: false,
  },
];

export function ModelSelector({ value, onChange, className }: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [models, setModels] = useState<AiModelInfo[]>(FALLBACK_MODELS);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Fetch models from API
  useEffect(() => {
    modelsApi.getAll()
      .then(setModels)
      .catch((err) => {
        console.warn('Failed to fetch models, using fallback:', err);
      });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate dropdown direction when opening
  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 400; // approximate max height
      setOpenUpward(spaceBelow < dropdownHeight);
    }
    setIsOpen(!isOpen);
  };

  const selectedModel = models.find((m) => m.id === value) || models.find(m => m.isGlobalDefault) || models[0];

  // Group models by provider - DeepSeek first (it's the default)
  const groupedModels = models.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<string, AiModelInfo[]>);

  // Order: Deepseek first, then Claude
  const providerOrder = ['Deepseek', 'Claude'];
  const orderedProviders = providerOrder.filter(p => groupedModels[p]);

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
          'bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-600',
          'hover:border-gray-300 dark:hover:border-dark-500',
          isOpen && 'ring-2 ring-primary-500 border-primary-500'
        )}
      >
        <ModelIcon model={selectedModel} className="w-4 h-4" />
        <span className="font-medium text-sm text-gray-700 dark:text-gray-300">
          {selectedModel?.name || 'Select Model'}
        </span>
        <ChevronDown className={cn(
          'w-4 h-4 text-gray-400 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={cn(
          'absolute left-0 w-96 z-50',
          'bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-200 dark:border-dark-700',
          'max-h-[400px] overflow-y-auto',
          openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
        )}>
          {orderedProviders.map((provider) => (
            <div key={provider}>
              {/* Provider Header */}
              <div className="px-3 py-2 bg-gray-50 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700 sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {provider}
                  </span>
                  {provider === 'Deepseek' && (
                    <span className="text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded">
                      Most Affordable
                    </span>
                  )}
                </div>
              </div>

              {/* Models */}
              {groupedModels[provider].map((model) => (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => {
                    onChange(model.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-start gap-3 p-3 text-left transition-colors',
                    'hover:bg-gray-50 dark:hover:bg-dark-700',
                    value === model.id && 'bg-primary-50 dark:bg-primary-900/20'
                  )}
                >
                  <ModelIcon model={model} className="w-5 h-5 mt-0.5 flex-shrink-0" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn(
                        'font-medium text-sm',
                        value === model.id
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-white'
                      )}>
                        {model.name}
                      </span>
                      {model.isGlobalDefault && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                          <Star className="w-2.5 h-2.5" />
                          Recommended
                        </span>
                      )}
                      {model.isDefault && !model.isGlobalDefault && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded">
                          Default
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                      {model.description}
                    </p>

                    {/* Capability Badges */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <CapabilityBadge
                        icon={<Zap className="w-3 h-3" />}
                        label={model.speedTier === 'fast' ? 'Fast' : model.speedTier === 'slow' ? 'Slow' : 'Medium'}
                        active={model.speedTier === 'fast'}
                      />
                      <CapabilityBadge
                        icon={<DollarSign className="w-3 h-3" />}
                        label={model.costTier <= 2 ? 'Cheap' : model.costTier >= 4 ? 'Premium' : 'Standard'}
                        active={model.costTier <= 2}
                      />
                      {model.supportsTools && (
                        <CapabilityBadge
                          icon={<Wrench className="w-3 h-3" />}
                          label="Tools"
                          active
                        />
                      )}
                      {model.supportsThinking && (
                        <CapabilityBadge
                          icon={<Brain className="w-3 h-3" />}
                          label="Thinking"
                          active
                        />
                      )}
                    </div>

                    {/* Context Window */}
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">
                      {Math.round(model.contextWindow / 1000)}K context
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ModelIcon({ model, className }: { model: AiModelInfo; className?: string }) {
  if (model.provider === 'Claude') {
    return <Brain className={cn('text-orange-500', className)} />;
  }
  return <Sparkles className={cn('text-blue-500', className)} />;
}

function CapabilityBadge({
  icon,
  label,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
}) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
      active
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
        : 'bg-gray-100 dark:bg-dark-600 text-gray-500 dark:text-gray-400'
    )}>
      {icon}
      {label}
    </span>
  );
}
